import log from '../plugins/log'
import axios from 'axios'
import LocalStorage from '../plugins/localStorage'
import { config } from '../boot/config'
import { Utils } from '../lib/utils'
import { useLoading as LoadingSpinner } from '../plugins/loading'
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface'

if (!config.main.service_url) throw new Error('Error parameter is not specified, check config file parameter: service_url')

// const {showLoading, hideLoading} = LoadingSpinner()

const axiosConfig = {
	maxContentLength: 104857600, //100mb
	maxBodyLength: 104857600, // 100mb
	withCredentials: true
}

const instance = axios.create({
	baseURL: config.main.service_url
})

instance.interceptors.request.use(
	(config) => {
		const access_token = LocalStorage.get('access_token', null)
		if (access_token) config.headers.Authorization = `Bearer ${access_token}`
		const csrfToken = LocalStorage.get('token', null)
		if (csrfToken) config.headers['X-CSRF-TOKEN'] = csrfToken
		// showLoading()
		return config
	},
	(error) => {
		// hideLoading()
		return Promise.reject(error)
	}
)

const accessDeniedNotification = () => {
	// Notify.error('Access Denied')
}

instance.interceptors.response.use(
	(response) => {
		// hideLoading()
		const pathname = window.location.pathname
		if (response && response.data && response.data.error && response.data.error == 'Session expired') {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			window.location = `/login?redirect=${pathname}`
		}
		if (response && response.data && response.data.code && response.data.code == 401 && response.data.status == 'Unauthorized') {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			window.location = `/login?redirect=${pathname}`
		}
		if (response && response.data && response.data.code && response.data.code == 403) {
			accessDeniedNotification()
		}
		return response
	},
	(error) => {
		// hideLoading()
		const pathname = window.location.pathname
		const response = error.response
		if (response && response.status == 401) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			window.location = `/login?redirect=${pathname}`
		}
		if (response && response.data && response.status == 403) {
			accessDeniedNotification()
		}
		return Promise.reject(error)
	}
)

export interface IDictionary {
	[id: string]: string | number | boolean | null
}

export default class HttpClient {
	axios: any

	constructor() {
		this.axios = instance
	}

  async get<TOut>(url: string, params?: IDictionary | null): Promise<IApiResponseGenericDTO<TOut>> {
		try {
			if (!params) params = null
			const options = {
				method: 'get',
				url,
				params: params
			}

			Object.assign(options, axiosConfig)

			log.trace('REQUEST DATA', '', options)
			const response = await this.axios(options)
			log.trace('RESPONSE DATA', response.data)
			return Utils.createResponse<TOut>(true, response.data)
		} catch (e: any) {
			const data = e?.response?.data
			const error = `Request execution error  HttpClient get, Error: ${e.message}, data: ${JSON.stringify(data)}`
			log.error(error, e)
			if (e.response) {
				const response = e.response
				if (e.response.data && typeof e.response.data == 'object') e = JSON.stringify(e.response.data)
				else if (e.response.data) e = e.response.data
				return Utils.createResponse(false, response.data)
			}
			return Utils.createResponse(false, e)
		}
	}

  async post<TIn, TOut>(url: string, data: TIn, params?: IDictionary | null): Promise<IApiResponseGenericDTO<TOut>> {
		try {
			if (!data) throw new Error('Error data is undefined')
			if (!params) params = null

			const options = {
				method: 'post',
				url,
				data: data,
				params: params
			}

			Object.assign(options, axiosConfig)

			log.trace('REQUEST DATA', '', options)
			const response = await this.axios(options)
			log.trace('RESPONSE DATA', response.data)
			return Utils.createResponse<TOut>(true, response.data)
		} catch (e: any) {
			const data = e?.response?.data
			const error = `Request execution error  HttpClient post, Error: ${e.message}, data: ${JSON.stringify(data)}`
			log.error(error, e)
			if (e.response) {
				const response = e.response
				if (e.response.data && typeof e.response.data == 'object') e = JSON.stringify(e.response.data)
				else if (e.response.data) e = e.response.data
				return Utils.createResponse(false, response.data)
			}
			return Utils.createResponse(false, e)
		}
	}

  async getBlob<TIn>(url: string, data: TIn, params?: IDictionary | null, filename = 'report.xlsx' ): Promise<void> {
		try {
			if (!data) throw new Error('Error data is undefined');
			if (!params) params = null;
	
			const options = {
				url,
				method: 'post',
				data: data,
				params: params,
				responseType: 'blob', 
				headers: {
					'Content-Type': 'application/json',
				},
			};

			Object.assign(options, axiosConfig)
	
      const response = await this.axios(options);
      log.trace('RESPONSE DATA', response.data)
      
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const utf8FilenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
        const asciiFilenameMatch = contentDisposition.match(/filename="(.+?)"/);

        if (utf8FilenameMatch) {
          filename = decodeURIComponent(utf8FilenameMatch[1]);
        } else if (asciiFilenameMatch) {
          filename = asciiFilenameMatch[1];
        }
      }

			const URL = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = URL;
      link.setAttribute('download', filename)
			document.body.appendChild(link);
			link.click()
			document.body.removeChild(link)
		} catch (e: any) {
			const error = `Request execution error: ${e.message}`
			log.error(error, e)
			throw new Error(e);
		}
	}

  async postFormData<TIn, TOut>(url: string, data?: TIn | null, params?: IDictionary | null): Promise<IApiResponseGenericDTO<TOut>> {
		try {
			if (!data) data = null
			if (!params) params = null

			const options = {
				method: 'post',
				url,
				data: data,
				params: params,
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			}
			Object.assign(options, axiosConfig)

			log.trace('REQUEST DATA', '', options)
			const response = await this.axios(options)
			log.trace('RESPONSE DATA', response.data)
			return Utils.createResponse<TOut>(true, response.data)
		} catch (e: any) {
			const data = e?.response?.data
			const error = `Request execution error  HttpClient post, Error: ${e.message}, data: ${JSON.stringify(data)}`
			log.error(error, e)
			if (e.response) {
				const response = e.response
				if (e.response.data && typeof e.response.data == 'object') e = JSON.stringify(e.response.data)
				else if (e.response.data) e = e.response.data
				return Utils.createResponse(false, response.data)
			}
			return Utils.createResponse(false, e)
		}
	}

  async put<TIn, TOut>(url: string, data: TIn, params?: IDictionary | null): Promise<IApiResponseGenericDTO<TOut>> {
		try {
			if (!data) throw new Error('Error data is undefined')
			if (!params) params = null

			const options = {
				method: 'put',
				url,
				data: data,
				params: params
			}

			Object.assign(options, axiosConfig)

			log.trace('REQUEST DATA', '', options)
			const response = await this.axios(options)
			log.trace('RESPONSE DATA', response.data)
			return Utils.createResponse<TOut>(true, response.data)
		} catch (e: any) {
			const data = e?.response?.data
			const error = `Request execution error  HttpClient put, Error: ${e.message}, data: ${JSON.stringify(data)}`
			log.error(error, e)
			if (e.response) {
				const response = e.response
				if (e.response.data && typeof e.response.data == 'object') e = JSON.stringify(e.response.data)
				else if (e.response.data) e = e.response.data
				return Utils.createResponse(false, response.data)
			}
			return Utils.createResponse(false, e)
		}
	}

  async delete<TIn, TOut>(url: string, data?: TIn | null, params?: IDictionary | null): Promise<IApiResponseGenericDTO<TOut>> {
		try {
			if (!data) data = null
			if (!params) params = null

			const options = {
				method: 'delete',
				url,
				data: data,
				params: params
			}

			Object.assign(options, axiosConfig)

			log.trace('REQUEST DATA', '', options)
			const response = await this.axios(options)
			log.trace('RESPONSE DATA', response.data)
			return Utils.createResponse<TOut>(true, response.data)
		} catch (e: any) {
			const data = e?.response?.data
			const error = `Request execution error  HttpClient delete, Error: ${e.message}, data: ${JSON.stringify(data)}`
			log.error(error, e)
			if (e.response) {
				const response = e.response
				if (e.response.data && typeof e.response.data == 'object') e = JSON.stringify(e.response.data)
				else if (e.response.data) e = e.response.data
				return Utils.createResponse(false, response.data)
			}
			return Utils.createResponse(false, e)
		}
	}
}

export { HttpClient, instance }
